import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { OriginMark } from '@/components/admin/origin-mark';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { NoyauPill } from '@/components/admin/sessions/noyau-pill';
import { PersonName } from '@/components/admin/sessions/person-name';
import { ReplayPill } from '@/components/admin/sessions/replay-pill';
import { sessionPurgeDate } from '@/components/admin/sessions/session-delete-dialog';
import { MISSING_DURATION } from '@/lib/duration-label';
import { reviewMark } from '@/lib/review-entry';
import { interruptionAuthor } from '@/lib/session-interruption';
import {
  costLabel,
  sessionDate,
  sessionDurationLabel,
  sessionShortMoment,
  sessionStatusLabel,
  sessionStatusTone,
} from '@/lib/session-labels';
import { isProdOrigin } from '@/lib/session-origin';
import { cn } from '@/lib/utils';
import type { SessionRow } from '@matter_extractor/shared';
import { Link } from '@tanstack/react-router';

/**
 * La liste de §12.1 : une ligne par session, huit colonnes, tri fixe.
 *
 * ## Ce qu'une ligne porte, et pourquoi chaque chose est là où elle est
 *
 * Date, expérience, lien, statut, noyau, tours, durée, coût - et trois choses
 * qui ne sont pas des colonnes :
 *
 *   - la **ligne de référents** sous le nom de l'expérience, qui nomme le
 *     protocole et le persona FIGÉS. Sans elle, filtrer par protocole retient
 *     des lignes qui ne disent pas pourquoi elles sont là ;
 *   - le **contexte du statut** sous la pastille - la page et l'étape d'une
 *     session en cours, la cause d'une fermeture, rien pour les deux autres.
 *     Une colonne « cause » serait vide sur les trois quarts des lignes ;
 *   - le **nom de la personne** sous l'heure (§9.2 et §12.1 de la refonte,
 *     H75 ; maquette `SessionListe`), quand l'expérience figée marque un champ
 *     « Nom de la personne dans Sessions » et que la session l'a rempli -
 *     coupé à vingt caractères, et lu entier dans sa bulle quand il déborde
 *     (`person-name.tsx`).
 *
 * La **marque de relecture** (relecture.md §13, issue #187) vient juste sous la
 * ligne de référents, sur toute session dont la relecture porte au moins un
 * élément : « relecture · patrick, alice », les parties locales des emails qui
 * ont posé quelque chose. Elle dit QUI a relu et jamais ce que la relecture
 * conclut : la colonne Noyau reste celle de la machine.
 *
 * La colonne **Lien** d'un REJEU (rejeu.md §12, #193), qui n'a pas de lien,
 * porte à sa place la pastille violette « rejeu · strict » (ou « complété ») et,
 * dessous, en petit, le libellé de la source ; le prénom saisi au consentement
 * reste sous la date, comme sur toute session. Une session ordinaire ne change
 * pas d'un caractère.
 *
 * La colonne **Noyau** (noyau.md §9.2, #152) porte le noyau RETENU en pastille à
 * son triplet, le nom en bulle, et un tiret sur une ligne qui n'en a pas - le
 * tiret des autres colonnes vides de la maquette, qui ne dit rien de plus.
 *
 * La version jouée s'écrit `v3` et jamais un identifiant de version : §4 fait du
 * NUMÉRO ce qu'une session fige, et un identifiant dans une colonne est une
 * chaîne que personne ne compare d'un coup d'œil.
 *
 * ## Tri FIXE, de la plus récente à la plus ancienne
 *
 * Aucune colonne ne se clique pour trier (§12.1) : « cette liste sert à
 * retrouver une session, pas à classer un corpus - un tri par colonne ferait
 * revenir par la fenêtre le tableau de bord que §12.5 retire ». L'ordre vient du
 * serveur, et rien ici ne le retouche.
 *
 * ## `?session=` MARQUE une ligne, il ne filtre pas
 *
 * « Un lien collé dit "regarde celle-ci" et pas "ne montre que celle-ci", et les
 * voisines sont la moitié de ce qui rend une ligne lisible » (§12.1).
 *
 * ## La ligne ENTIÈRE ouvre la session
 *
 * Un `<tr>` ne peut pas être un lien, donc le nom de l'expérience en porte un
 * vrai - typé contre l'arbre de routes - et son `::after` recouvre la rangée.
 * C'est un ancrage réel : il a une adresse, il s'ouvre dans un onglet, un
 * lecteur d'écran l'annonce. Les boutons de la rangée passent au-dessus par
 * `relative z-10`, sinon le voile les avalerait.
 *
 * Le `relative` de la RANGÉE est ce qui borne ce voile, et il ne se retire pas :
 * sans lui, le bloc conteneur remonterait au défileur - qui porte le sien pour
 * la légende `sr-only` (BUILD-916) - et le lien de la première ligne
 * recouvrirait le tableau entier.
 */

export interface SessionsTableProps {
  readonly rows: readonly SessionRow[];
  /** Le `?session=` de §12.1 - la ligne que désigne un lien collé. */
  readonly focusedId: string | null;
  /** La corbeille REMPLACE la liste (§12.4) : les lignes se grisent et se restaurent. */
  readonly trash: boolean;
  readonly onDelete: (row: SessionRow) => void;
  readonly onRestore: (row: SessionRow) => void;
  /** La ligne dont la restauration est en vol, pour que son bouton se taise. */
  readonly restoringId: string | null;
}

/** Les huit en-têtes, et lequel est aligné à droite parce qu'il compte. */
const COLUMNS = [
  { label: 'Date', numeric: false },
  { label: 'Expérience', numeric: false },
  { label: 'Lien', numeric: false },
  { label: 'Statut', numeric: false },
  { label: 'Noyau', numeric: false },
  { label: 'Tours', numeric: true },
  { label: 'Durée', numeric: true },
  { label: 'Coût', numeric: true },
] as const;

/**
 * Le contexte sous la pastille : la position pour une session en cours, la cause
 * pour une fermeture, « par alice · 21 oct. 12:12 » pour une interruption
 * (issue #199, interruption.md §8), rien pour les deux autres (§12.1).
 *
 * Exporté parce que c'est la seule phrase de ce fichier qui se teste sans DOM,
 * et parce qu'elle est le lieu d'une confusion facile : une session `terminée`
 * ou `expirée` n'a pas de cause - `closeReason` est nul sur tout statut qui
 * n'est pas `closed` - donc écrire « rien » ici est une décision et pas un
 * oubli.
 */
export function sessionStatusContext(row: SessionRow): string | null {
  if (row.status === 'closed') return row.closeReason;
  // « par alice · 21 oct. 12:12 » : qui a posé le geste et quand, là où une fermeture dit sa cause.
  if (row.status === 'interrupted') {
    const author = interruptionAuthor(row.endedBy);
    const moment = row.endedAt === null ? null : sessionShortMoment(row.endedAt);
    const parts = [author === null ? null : `par ${author}`, moment].filter((one) => one !== null);
    return parts.length === 0 ? null : parts.join(' · ');
  }
  if (row.status !== 'in-progress') return null;
  // Une session de la prod en cours est ENCORE jouée là-bas : l'atelier la relit en direct,
  // avec au plus une heure de retard, et la ligne le dit (maquette `atelier.html`, section 3).
  const where = isProdOrigin(row) ? 'sur la prod' : null;
  const page = row.currentPageName === null ? null : `page ${row.currentPageName}`;
  if (row.currentStep === null) {
    return [where, page].filter((one) => one !== null).join(' · ') || null;
  }
  const step = `étape ${row.currentStep.index} · ${row.currentStep.name}`;
  const turn = `tour ${row.currentStep.turnInStep}`;
  return [where, page, step, turn].filter((one) => one !== null).join(' · ');
}

/**
 * Le coût d'une ligne - un TIRET quand la session n'a pas eu de tour.
 *
 * « Zéro euro est une mesure, l'absence de conversation n'en est pas une »
 * (maquette, « Ce que la maquette fixe »). Une session sans tour n'a rien coûté
 * parce qu'elle n'a rien appelé, et l'écrire `0,00 €` inviterait à la comparer
 * à une session qui a parlé pour presque rien.
 */
export function rowCostLabel(row: SessionRow): string {
  return row.turnCount === 0 ? MISSING_DURATION : costLabel(row.costEur);
}

export function SessionsTable({
  rows,
  focusedId,
  trash,
  onDelete,
  onRestore,
  restoringId,
}: SessionsTableProps) {
  return (
    <div className="relative overflow-x-auto">
      <table data-testid={SESSIONS_TEST_IDS.list} className="w-full border-collapse text-sm">
        <caption className="sr-only">
          Sessions, de la plus récente à la plus ancienne{trash ? ' - corbeille' : ''}
        </caption>
        <thead>
          <tr className="border-gray-200 border-b text-left text-gray-600">
            {COLUMNS.map((column) => (
              <th
                key={column.label}
                scope="col"
                className={cn(
                  'py-2 font-semibold text-xs uppercase tracking-wide',
                  column.numeric ? 'pl-3 text-right' : 'pr-3',
                )}
              >
                {column.label}
              </th>
            ))}
            {/* La colonne d'action ne porte pas d'en-tête visible : le mot
                serait dit deux fois, une fois au-dessus du bouton et une fois
                dessus. `sr-only` plutôt que vide, pour qu'elle reste annoncée. */}
            <th scope="col" className="py-2 pl-3">
              <span className="sr-only">Gestes</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <SessionRowLine
              key={row.id}
              row={row}
              focused={row.id === focusedId}
              trash={trash}
              onDelete={onDelete}
              onRestore={onRestore}
              restoring={row.id === restoringId}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Les référents figés, nommés sous l'expérience - « Entretien fondateur v5 · Claire empathique v2 ». */
function referents(row: SessionRow): string {
  const one =
    row.protocolName === null
      ? 'sans protocole'
      : `${row.protocolName}${row.protocolVersionNumber === null ? '' : ` v${row.protocolVersionNumber}`}`;
  const two =
    row.personaName === null
      ? 'sans persona'
      : `${row.personaName}${row.personaVersionNumber === null ? '' : ` v${row.personaVersionNumber}`}`;
  return `${one} · ${two}`;
}

function SessionRowLine({
  row,
  focused,
  trash,
  onDelete,
  onRestore,
  restoring,
}: {
  row: SessionRow;
  focused: boolean;
  trash: boolean;
  onDelete: (row: SessionRow) => void;
  onRestore: (row: SessionRow) => void;
  restoring: boolean;
}) {
  const context = sessionStatusContext(row);
  const retained = row.noyauCodes?.retained ?? null;
  const trial = retained === null ? row.noyauTrial : null;
  const review = reviewMark(row.reviewAuthors);
  const replay = row.replay ?? null;
  const numeric = 'py-3 pl-3 text-right tabular-nums whitespace-nowrap';

  return (
    <tr
      data-testid={SESSIONS_TEST_IDS.row}
      data-session-id={row.id}
      data-focused={focused ? 'true' : undefined}
      data-trashed={trash ? 'true' : undefined}
      className={cn(
        'relative border-gray-100 border-b align-top',
        // Le fond du survol est posé sur la rangée entière, parce que c'est la
        // rangée entière qui ouvre la session.
        'hover:bg-gray-50',
        focused && 'bg-bleu-nuit-tint hover:bg-bleu-nuit-tint',
        trash && 'text-gray-500',
      )}
    >
      <td className="whitespace-nowrap py-3 pr-3 text-gray-700">
        {sessionDate(row.startedAt)}
        {row.personName === null ? null : (
          <PersonName name={row.personName.value} sessionId={row.id} />
        )}
      </td>
      <td className="py-3 pr-3">
        <Link
          to="/admin/sessions/$sessionId"
          params={{ sessionId: row.id }}
          /*
            Le `::after` qui recouvre la rangée - c'est lui, « la ligne entière
            ouvre la session ». Il tient parce que le `<tr>` est `relative` :
            l'ancrage reste un ancrage, avec son adresse et son clic milieu.
          */
          className={cn(
            'font-semibold after:absolute after:inset-0 after:content-[""] hover:underline',
            trash ? 'text-gray-500' : 'text-bleu-nuit',
          )}
        >
          {row.experienceName ?? 'Expérience retirée'}
        </Link>
        {/*
          « vN » seul : une entrée de lien vise désormais latest ou une version précise (#192, V5,
          V16), et la session a figé la sienne - la version dit tout, « production » ou
          « développement » ne disait plus rien de plus. La marque « prod » se pose à côté.
        */}
        <span className="ml-2 whitespace-nowrap text-gray-500 text-xs">
          {row.experienceVersionNumber === null
            ? 'version illisible'
            : `v${row.experienceVersionNumber}`}
        </span>
        {isProdOrigin(row) ? (
          <span className="ml-1.5">
            <OriginMark testId={SESSIONS_TEST_IDS.rowOrigin} />
          </span>
        ) : null}
        <p className="text-gray-500 text-xs">{referents(row)}</p>
        {review === null ? null : (
          <span
            data-testid={SESSIONS_TEST_IDS.rowReviewMark}
            className="mt-0.5 inline-flex items-center rounded-sm border border-gray-300 px-1.5 text-gray-700 text-xs leading-4"
          >
            {review}
          </span>
        )}
      </td>
      <td className="py-3 pr-3 text-gray-700">
        {replay === null ? (
          (row.linkName ?? MISSING_DURATION)
        ) : (
          <>
            <ReplayPill mode={replay.mode} testId={SESSIONS_TEST_IDS.rowReplay} />
            <span
              data-testid={SESSIONS_TEST_IDS.rowReplaySource}
              className="mt-0.5 block max-w-[22ch] break-words text-gray-600 text-xs leading-4"
            >
              {replay.sourceLabel}
            </span>
          </>
        )}
      </td>
      <td className="py-3 pr-3">
        <StatusBadge tone={sessionStatusTone(row.status)} pulse={row.status === 'in-progress'}>
          {sessionStatusLabel(row.status)}
        </StatusBadge>
        {context === null ? null : (
          <span className="mt-1 block max-w-[26ch] text-gray-600 text-xs leading-4">{context}</span>
        )}
        {trash && row.deletedAt !== null ? (
          <span className="mt-1 block text-gray-600 text-xs leading-4">
            À la corbeille · effacée le {sessionPurgeDate(row.deletedAt)}
          </span>
        ) : null}
      </td>
      <td className="whitespace-nowrap py-3 pr-3">
        {retained !== null ? (
          <NoyauPill code={retained} sessionId={row.id} />
        ) : trial !== null ? (
          <NoyauPill code={trial} sessionId={row.id} trial />
        ) : (
          <span className="text-gray-400">{MISSING_DURATION}</span>
        )}
      </td>
      <td className={cn(numeric, row.turnCount === 0 ? 'text-gray-400' : 'text-gray-700')}>
        {row.turnCount}
      </td>
      <td className={cn(numeric, 'text-gray-700')}>{sessionDurationLabel(row.durationMs)}</td>
      <td className={cn(numeric, row.turnCount === 0 ? 'text-gray-400' : 'text-gray-700')}>
        {rowCostLabel(row)}
      </td>
      <td className="py-3 pl-3 text-right">
        {/*
          `relative z-10` : le voile de la ligne recouvre toute la rangée, donc
          un bouton posé dessous serait cliquable à l'œil et pas à la souris.
        */}
        {trash ? (
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.rowRestore}
            disabled={restoring}
            onClick={() => onRestore(row)}
            className="relative z-10 cursor-pointer whitespace-nowrap font-semibold text-bleu-nuit text-sm underline underline-offset-2 hover:text-bleu-nuit-deep disabled:cursor-not-allowed disabled:text-gray-400 disabled:no-underline"
          >
            {restoring ? 'Restauration…' : 'Restaurer'}
          </button>
        ) : (
          <button
            type="button"
            data-testid={SESSIONS_TEST_IDS.rowDelete}
            onClick={() => onDelete(row)}
            className="relative z-10 cursor-pointer whitespace-nowrap text-gray-600 text-sm underline underline-offset-2 hover:text-rouge-deep"
          >
            Supprimer
          </button>
        )}
      </td>
    </tr>
  );
}
